import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import React, { Fragment, memo } from 'react';
import { FormattedMessage } from 'react-intl';

import useDocumentCard from '../../hooks/useDocumentCard';
// import { FormattedMessage } from 'react-intl';
// import useFontSize from '../../hooks/useFontSize';
// import * as AppPropTypes from '../../lib/PropTypes';
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import BasicButton from '../buttons/BasicButton';
import LocationsCountIcon from '../icons/LocationCountIcon';
import Blocked from '../partials/Blocked';
import Category from '../partials/Category';
import DocumentCardMeta from '../partials/DocumentCardMeta';
import DocumentCardThumbnail from '../partials/DocumentCardThumbnail';
import Surtitle from '../partials/Surtitle';
import CardTitle from '../typography/CardTitle';
import HorizontalCard from './HorizontalCard';

import styles from '../../styles/cards/document-horizontal-card.module.css';

export interface DocumentHorizontalCardProps extends Omit<Partial<DocumentSnippet>, 'type'> {
    type: DocumentTypeString | 'native_ad' | null;
    externalUrl?: string | null;
    linkQuery?: Record<string, unknown> | null;
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize;
    ratio?: CardRatio;
    height?: number | null;
    placeholder?: boolean;
    responsive?: boolean;
    imageLoading?: Loading;
    withBorder?: boolean;
    withLocations?: boolean;
    withoutAuthor?: boolean;
    withAuthorImage?: boolean;
    withCategory?: boolean;
    withoutSurtitle?: boolean;
    withoutDuration?: boolean;
    withoutTime?: boolean;
    withBrandIcon?: boolean;
    withoutTypeIcon?: boolean;
    withoutSerie?: boolean;
    withoutCollection?: boolean;
    withEpisode?: boolean;
    withoutAdNotice?: boolean;
    className?: string | null;
    onClick?: ((event: React.MouseEvent<HTMLElement>) => void) | null;
}

const DocumentHorizontalCard = ({
    id = null,
    type = null,
    brands = null,
    locations = null,
    slug = null,
    linkQuery = null,
    title = null,
    surtitle = null,
    access = null,
    image = null,
    coverImage = null,
    categories = null,
    credits = null,
    authors = null,
    sponsors = null,
    duration = null,
    date = null,
    topic = null,
    format = null,
    topics = null,
    formats = null,
    collection = null,
    season = null,
    episode = null,
    micromagNumber = null,
    externalUrl = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    height: providedHeight = null,
    placeholder = false,
    responsive = false,
    imageLoading = undefined,
    withBorder = false,
    withLocations = false,
    withCategory = false,
    withoutAuthor = false,
    withAuthorImage = false,
    withoutSurtitle = false,
    withoutDuration = false,
    withoutTime = false,
    withBrandIcon = false,
    withoutTypeIcon = false,
    withoutSerie = false,
    withoutCollection = false,
    withoutAdNotice = false,
    withEpisode = false,
    className = null,
    onClick = null,
}: DocumentHorizontalCardProps) => {
    const theme = normalizeCardTheme(providedTheme);
    const url = useUrlGenerator();
    const {
        href,
        category,
        author,
        brandHandle,
        sponsor,
        blocked,
        unblocked,
        progress,
        surtitle: finalSurtitle,
    } = useDocumentCard(
        {
            id,
            type,
            slug,
            externalUrl,
            surtitle,
            brands,
            categories,
            credits,
            authors,
            sponsors,
            topic,
            format,
            topics,
            formats,
            collection,
            season,
            episode,
            micromagNumber,
            date,
            access,
        },
        {
            linkQuery,
            placeholder,
            surtitle: {
                withoutSerie,
                withCategory: !withCategory,
                withoutCollection,
                withoutMicromagDate: true,
                withoutEpisode: !withEpisode,
            },
        },
    );

    const isNativeAd = type === 'native_ad';
    const isMenu = theme === 'menu';
    const isMicromag = type === 'micromag';
    const isBig = size === 'big';
    const isSmall = size === 'small';
    const isTiny = size === 'tiny';
    const isBox = theme !== null && theme.indexOf('box') === 0;

    let defaultRatio = 1;
    if (theme === 'menu' || type === 'article') {
        defaultRatio = 5 / 4;
    } else if (type === 'video') {
        defaultRatio = 4 / 5;
    } else if (type === 'micromag') {
        defaultRatio = 4 / 6;
    }

    const thumbnailRatio = getRatio(ratio, defaultRatio);

    let defaultHeight = 100;
    if (isBig) {
        defaultHeight = 150;
    } else if (isSmall) {
        defaultHeight = 70;
    } else if (isTiny) {
        defaultHeight = 50;
    }

    const height = providedHeight || defaultHeight;

    const showCategory = withCategory && !isTiny;
    const showSurtitle = !withoutSurtitle && !isTiny;
    const Wrapper = withLocations ? 'div' : Fragment;

    return (
        <Wrapper
            {...(withLocations
                ? {
                      className: classNames([styles.wrapper, className]),
                  }
                : null)}
        >
            <HorizontalCard
                href={href || '#'}
                external={externalUrl !== null}
                onClick={!placeholder ? onClick : null}
                thumbnail={
                    <DocumentCardThumbnail
                        type={type}
                        image={image}
                        coverImage={coverImage}
                        ratio={ratio}
                        duration={duration}
                        withoutDuration={withoutDuration || isTiny}
                        withDurationInImage
                        withIcon={!placeholder && !isTiny}
                        withBrandIcon={withBrandIcon}
                        withoutTypeIcon={withoutTypeIcon}
                        brand={brandHandle}
                        width="100%"
                        // width={thumbnailRatio * height}
                        height="100%"
                        size="small"
                        progress={progress}
                        // responsive={responsive}
                        withoutSize={responsive}
                        withPlaceholder={placeholder}
                        className={classNames([
                            styles.thumbnail,
                            {
                                [styles.gradiant]: isMenu && isMicromag,
                            },
                        ])}
                        loading={imageLoading}
                        iconClassName={styles.thumbnailIcon}
                        pictureClassName={classNames([
                            styles.picture,
                            {
                                [styles.containImage]:
                                    isMenu && (type === 'micromag' || type === 'podcast'),
                            },
                        ])}
                        durationClassName={styles.duration}
                    />
                }
                thumbnailRatio={thumbnailRatio}
                height={providedHeight || height}
                responsive={responsive}
                className={classNames([
                    styles.container,
                    {
                        [styles.box]: isBox,
                        [styles.isNativeAd]: isNativeAd,
                        [styles.withAdNotice]: !withoutAdNotice && isNativeAd,
                        [styles.hasSponsor]: sponsor !== null,
                        [styles.hasSurtitle]: finalSurtitle !== null && showSurtitle,
                        [styles.responsive]: responsive,
                        [styles.withBorder]: withBorder && !isBox,
                    },
                    className,
                ])}
                theme={theme || 'none'}
                size={size || 'default'}
                thumbnailClassName={styles.thumbnailContainer}
                innerClassName={styles.inner}
                labelClassName={styles.label}
            >
                {isNativeAd && !withoutAdNotice ? (
                    <div className={styles.adNotice}>
                        <FormattedMessage defaultMessage="Publicité" description="Card label" />
                    </div>
                ) : null}
                <div className={styles.top}>
                    {(category !== null || placeholder) && showCategory ? (
                        <Category
                            {...category}
                            placeholder={placeholder}
                            className={styles.category}
                        />
                    ) : null}

                    {finalSurtitle !== null && showSurtitle ? (
                        <Surtitle tag="h5" surtitle={finalSurtitle} className={styles.surtitle} />
                    ) : null}
                    {blocked ? (
                        <Blocked short unblocked={unblocked} className={styles.blocked} />
                    ) : null}
                </div>

                <CardTitle
                    className={styles.title}
                    placeholder={placeholder}
                    placeholderLines={3}
                    title={title}
                >
                    {title}
                </CardTitle>

                <DocumentCardMeta
                    tag="span"
                    author={type !== 'micromag' ? author : null}
                    sponsor={sponsor}
                    date={date}
                    placeholder={placeholder}
                    withoutAuthor={withoutAuthor}
                    withoutAuthorImage={!withAuthorImage}
                    withoutTime={withoutTime}
                    className={styles.meta}
                    authorClassName={styles.author}
                    sponsorClassName={styles.sponsor}
                    timeClassName={styles.time}
                />
            </HorizontalCard>
            {withLocations && locations !== null && locations.length > 0 ? (
                <BasicButton
                    href={
                        slug !== null
                            ? url('map.document', {
                                  document: slug,
                              })
                            : null
                    }
                    className={classNames([
                        styles.container,
                        styles.locations,
                        {
                            [styles.box]: isBox,
                            [styles.hasSponsor]: sponsor !== null,
                            [styles.responsive]: responsive,
                            [styles.withBorder]: withBorder,
                        },
                        className,
                    ])}
                    data-card-presentation="horizontal"
                    data-card-theme={theme || 'none'}
                    data-card-size={size || 'default'}
                >
                    <LocationsCountIcon
                        iconCircleColor="transparent"
                        count={locations.length}
                        countClassName={styles.locationCount}
                        className={styles.locationIcon}
                    />
                </BasicButton>
            ) : null}
        </Wrapper>
    );
};

export default memo(DocumentHorizontalCard);
